import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Pagination from "react-js-pagination";
import { IoChevronBack } from "react-icons/io5";
import IconButton from "../../components/Button/IconButton";
import { Input, TextArea, Select } from "../../components/Form";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import ModalBox from "../../components/modal/ModalBox";

import {
  fetchInfluencerByIdApi,
  fetchInfluencerMetricsApi,
  fetchInfluencerLedgerApi,
  fetchInfluencerSettlementsApi,
  fetchSettlementPreviewApi,
  createSettlementApi,
} from "../../services/influencerManagement";

const TAB_DASHBOARD = "dashboard";
const TAB_LEDGER = "ledger";
const TAB_SETTLEMENTS = "settlements";

export default function InfluencerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inf, setInf] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_DASHBOARD);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Ledger
  const [ledger, setLedger] = useState([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerType, setLedgerType] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Settlements
  const [settlements, setSettlements] = useState([]);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settlePeriodStart, setSettlePeriodStart] = useState("");
  const [settlePeriodEnd, setSettlePeriodEnd] = useState("");
  const [settleRef, setSettleRef] = useState("");
  const [settleRemarks, setSettleRemarks] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [settleSubmitting, setSettleSubmitting] = useState(false);

  const pageSize = 10;

  // ── Load influencer + metrics ────────────────────────────────────────────────
  const loadBaseData = useCallback(async () => {
    showLoader();
    try {
      const [infRes, metricsRes] = await Promise.all([
        fetchInfluencerByIdApi(id),
        fetchInfluencerMetricsApi(id)
      ]);
      if (infRes.data.success) setInf(infRes.data.data);
      if (metricsRes.data.success) setMetrics(metricsRes.data.data);
    } catch (e) {
      toastMessage("Failed to load influencer data", "error");
    } finally {
      hideLoader();
    }
  }, [id]);

  useEffect(() => { loadBaseData(); }, [loadBaseData]);

  // ── Load ledger ───────────────────────────────────────────────────────────────
  const loadLedger = useCallback(async () => {
    try {
      const params = { page: ledgerPage - 1, pageSize };
      if (ledgerType) params.type = ledgerType;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const r = await fetchInfluencerLedgerApi(id, params);
      if (r.data.success) {
        setLedger(r.data.data.entries || []);
        setLedgerTotal(r.data.data.total || 0);
      }
    } catch (e) { console.error(e); }
  }, [id, ledgerPage, ledgerType, dateFrom, dateTo]);

  useEffect(() => { if (activeTab === TAB_LEDGER) loadLedger(); }, [activeTab, loadLedger]);

  // ── Load settlements ──────────────────────────────────────────────────────────
  const loadSettlements = useCallback(async () => {
    try {
      const r = await fetchInfluencerSettlementsApi(id);
      if (r.data.success) setSettlements(r.data.data.settlements || []);
    } catch (e) { console.error(e); }
  }, [id]);

  useEffect(() => { if (activeTab === TAB_SETTLEMENTS) loadSettlements(); }, [activeTab, loadSettlements]);

  // ── Preview settlement ────────────────────────────────────────────────────────
  const loadPreview = async () => {
    if (!settlePeriodStart || !settlePeriodEnd) return;
    setPreviewLoading(true);
    try {
      const r = await fetchSettlementPreviewApi(id, settlePeriodStart, settlePeriodEnd);
      if (r.data.success) setPreview(r.data.data);
    } catch (e) { toastMessage("Failed to load preview.", "error"); }
    setPreviewLoading(false);
  };

  // ── Create settlement ─────────────────────────────────────────────────────────
  const handleSettle = async (e) => {
    e.preventDefault();
    setSettleSubmitting(true);
    try {
      await createSettlementApi(id, {
        periodStart: settlePeriodStart,
        periodEnd: settlePeriodEnd,
        paymentReference: settleRef,
        remarks: settleRemarks,
      });
      toastMessage("Settlement created successfully!", "success");
      setShowSettleModal(false);
      loadSettlements();
      fetchInfluencerMetricsApi(id).then(r => { if (r.data.success) setMetrics(r.data.data); });
    } catch (e) {
      toastMessage(e?.response?.data?.message || "Settlement failed.", "error");
    }
    setSettleSubmitting(false);
  };

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

  if (!inf) return <>{loader}</>;

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <div className="d-flex align-items-center">
              <IconButton onClick={() => navigate(-1)} className="mr-2">
                <IoChevronBack />
              </IconButton>
              <h4 className="Title">{inf.name}'s Dashboard</h4>
            </div>
            <div className="block_user_button">
              <button className="btn btn-primary" onClick={() => setShowSettleModal(true)}>
                💰 Create Settlement
              </button>
            </div>
          </div>

          <div className="p-3" style={{ background: "#f8f9fa", borderRadius: "8px", marginBottom: "20px" }}>
            <div className="row align-items-center">
              <div className="col-auto">
                <div style={{
                  width: "60px", height: "60px", borderRadius: "50%",
                  background: "#CD2F38", color: "#fff", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: "24px", fontWeight: "bold"
                }}>
                  {inf.name?.[0]?.toUpperCase()}
                </div>
              </div>
              <div className="col">
                <h5 className="mb-0">{inf.name}</h5>
                <div className="text-muted">{inf.email} · {inf.phone}</div>
                <div className="mt-1">
                  <span className="badge badge-primary" style={{ background: "#6c63ff", color: "#fff" }}>
                    Earning Share: {inf.sharePercent}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="tabs_area">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === TAB_DASHBOARD ? "active" : ""}`} onClick={() => setActiveTab(TAB_DASHBOARD)}>📊 Overview</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === TAB_LEDGER ? "active" : ""}`} onClick={() => setActiveTab(TAB_LEDGER)}>📋 Ledger</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === TAB_SETTLEMENTS ? "active" : ""}`} onClick={() => setActiveTab(TAB_SETTLEMENTS)}>💳 Settlements</button>
              </li>
            </ul>
          </div>

          <div className="tab-content mt-4">
            {/* ── Dashboard Tab ── */}
            {activeTab === TAB_DASHBOARD && (
              <div>
                <div className="row">
                  {[
                    { label: "Total Earnings", value: fmt(metrics?.totalEarnings), icon: "💰", color: "#6c63ff" },
                    { label: "Total Refunds", value: fmt(metrics?.totalRefunds), icon: "↩️", color: "#e17055" },
                    { label: "Net Earnings", value: fmt(metrics?.netEarnings), icon: "✅", color: "#00b894" },
                    { label: "Total Recharges", value: metrics?.totalRecharges ?? "—", icon: "🔄", color: "#0984e3" },
                    { label: "Unique Users", value: metrics?.totalUsers ?? "—", icon: "👥", color: "#fd79a8" },
                    { label: "Recharge Volume", value: fmt(metrics?.totalRechargeAmount), icon: "📈", color: "#fdcb6e" },
                  ].map(m => (
                    <div key={m.label} className="col-md-4 mb-4">
                      <div className="card h-100 p-3" style={{ borderLeft: `5px solid ${m.color}`, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                        <div className="d-flex justify-content-between">
                          <div className="text-muted small font-weight-bold">{m.label.toUpperCase()}</div>
                          <div style={{ fontSize: "20px" }}>{m.icon}</div>
                        </div>
                        <h3 className="mt-2 mb-0" style={{ color: "#333" }}>{m.value}</h3>
                      </div>
                    </div>
                  ))}
                </div>

                {inf.bankDetails && (
                  <div className="card mt-2">
                    <div className="card-header bg-white">
                      <h6 className="mb-0">🏦 Bank / UPI Details</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        {[
                          ["Account Holder", inf.bankDetails.accountHolderName],
                          ["Account Number", inf.bankDetails.accountNumber],
                          ["IFSC Code", inf.bankDetails.ifscCode],
                          ["Bank Name", inf.bankDetails.bankName],
                          ["UPI ID", inf.bankDetails.upiId],
                        ].map(([k, v]) => v ? (
                          <div key={k} className="col-md-4 mb-2">
                            <div className="small text-muted">{k}</div>
                            <div>{v}</div>
                          </div>
                        ) : null)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Ledger Tab ── */}
            {activeTab === TAB_LEDGER && (
              <div>
                <div className="FilterArea mb-3">
                  <div className="row w-100">
                    <div className="col-md-3">
                      <Select className="form-control" value={ledgerType} onChange={e => { setLedgerType(e.target.value); setLedgerPage(1); }}>
                        <option value="">All Types</option>
                        <option value="EARNING">Earnings</option>
                        <option value="REFUND">Refunds</option>
                      </Select>
                    </div>
                    <div className="col-md-3">
                      <Input type="date" className="form-control" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setLedgerPage(1); }} />
                    </div>
                    <div className="col-md-3">
                      <Input type="date" className="form-control" value={dateTo} onChange={e => { setDateTo(e.target.value); setLedgerPage(1); }} />
                    </div>
                  </div>
                </div>

                <div className="content-table">
                  <table>
                    <thead>
                      <tr>
                        <th>S.No.</th><th>Date</th><th>User</th><th>Type</th><th>Base Amount</th><th>Earned</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.length > 0 ? (
                        ledger.map((e, index) => (
                          <tr key={index}>
                            <td>{findSerialNumber(index, ledgerPage, pageSize)}</td>
                            <td>{new Date(e.createdAt).toLocaleDateString("en-IN")}</td>
                            <td>
                              <strong>{e.user?.name || "—"}</strong>
                              <div className="small text-muted">{e.user?.details?.phone || ""}</div>
                            </td>
                            <td>
                              {e.type === "EARNING" ? (
                                <span className="text-success">💰 Earning</span>
                              ) : (
                                <span className="text-danger">↩️ Refund</span>
                              )}
                            </td>
                            <td>{e.rechargeBaseAmount ? fmt(e.rechargeBaseAmount) : "—"}</td>
                            <td>
                              <strong className={e.amount >= 0 ? "text-success" : "text-danger"}>
                                {e.amount >= 0 ? "+" : ""}{fmt(e.amount)}
                              </strong>
                            </td>
                            <td>
                              <span>
                                {e.status.toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={7} className="text-center py-4">No ledger entries found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {ledgerTotal > pageSize && (
                  <div className="pagination-wrapper mt-3">
                    <div className="Pagination">
                      <Pagination
                        activePage={ledgerPage}
                        itemsCountPerPage={pageSize}
                        totalItemsCount={ledgerTotal}
                        pageRangeDisplayed={5}
                        onChange={(p) => setLedgerPage(p)}
                        hideDisabled={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Settlements Tab ── */}
            {activeTab === TAB_SETTLEMENTS && (
              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th>S.No.</th><th>Period</th><th>Revenue</th><th>Refunds</th><th>Net Payout</th><th>Status</th><th>Payment Ref</th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlements.length > 0 ? (
                      settlements.map((s, index) => (
                        <tr key={s._id}>
                          <td>{index + 1}</td>
                          <td>
                            {new Date(s.periodStart).toLocaleDateString("en-IN")} — {new Date(s.periodEnd).toLocaleDateString("en-IN")}
                          </td>
                          <td className="text-success">+{fmt(s.totalEarning)}</td>
                          <td className="text-danger">-{fmt(s.totalRefunds)}</td>
                          <td><strong>{fmt(s.netPayout)}</strong></td>
                          <td>
                            <span className={`badge ${s.status === "paid" ? "badge-success" : "badge-secondary"}`}>
                              {s.status.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            {s.paymentReference || "—"}
                            <div className="small text-muted">{s.paidAt ? new Date(s.paidAt).toLocaleDateString("en-IN") : ""}</div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={7} className="text-center py-4">No settlements found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <ModalBox
        status={showSettleModal}
        onClose={() => setShowSettleModal(false)}
        title="💰 Create Settlement"
        closeIcon
        size="md"
        noFooter
      >
        <form onSubmit={handleSettle}>
          <div className="grid-responsive">
            <div className="form-group">
              <Input
                label="Period Start"
                type="date"
                required
                className="form-control"
                value={settlePeriodStart}
                onChange={(e) => setSettlePeriodStart(e.target.value)}
              />
            </div>
            <div className="form-group">
              <Input
                label="Period End"
                type="date"
                required
                className="form-control"
                value={settlePeriodEnd}
                onChange={(e) => setSettlePeriodEnd(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <button
                type="button"
                className="btn btn-outline-info btn-sm btn-block"
                onClick={loadPreview}
                disabled={previewLoading}
                style={{ borderRadius: "8px", fontWeight: "600", padding: "10px", width: "100%" }}
              >
                {previewLoading ? "Calculating..." : "🔍 Preview Pending Amount"}
              </button>
            </div>

            {preview && (
              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <div className="p-3 bg-light rounded border text-center" style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}>
                  <div className="text-muted small" style={{ fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.5px" }}>Pending Net Payout</div>
                  <h3 className="mb-0 mt-1" style={{ color: "#CD2F38", fontWeight: "700" }}>{fmt(preview.netPayout)}</h3>
                  <div className="small text-muted mt-1">{preview.count} transactions found</div>
                </div>
              </div>
            )}

            <div className="form-group">
              <Input
                label="Payment Reference"
                className="form-control"
                value={settleRef}
                onChange={(e) => setSettleRef(e.target.value)}
                placeholder="UTR / Transaction ID"
              />
            </div>
            <div className="form-group">
              <Input
                label="Remarks"
                className="form-control"
                value={settleRemarks}
                onChange={(e) => setSettleRemarks(e.target.value)}
                placeholder="e.g. Paid via Bank"
              />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1", marginTop: "1rem" }}>
              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={settleSubmitting}
                style={{ padding: "12px", fontWeight: "600" }}
              >
                {settleSubmitting ? "Processing..." : "✅ Mark as Paid & Settle"}
              </button>
            </div>
          </div>
        </form>
      </ModalBox>
    </>
  );
}
